import { colors, radius, space } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { Header2, Text } from '@ardoq/typography';
import { Component, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';
import { ErrorMessage } from './ErrorMessage';

export type ErrorBoundaryProps = PropsWithChildren<{
  errorContextDescription?: string;
  title?: string;
  description?: string;
  apologyText?: string;
  contactSupportText?: string;
  errorMessage?: ReactNode;
  recoverOnError?: (callback: () => any) => any;
  resetKeys?: Array<string | number>;
  /** Only rendered in development mode. */
  stackTraceLabel?: string;
  /** Only rendered in development mode. */
  stackTraceDevelopmentOnlyHint?: string;
  onError?: (error: Error) => void;

  /** @deprecated Either recover the error or rely on Sentry to catch the error after it's been re-thrown */
  logError?: (error: Error, message: string) => void;
}>;

type ErrorBoundaryState = {
  error: Error | null;
  recoveryAttempt: boolean;
  resetKeys?: any[];
};
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  // https://reactjs.org/docs/error-boundaries.html
  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  static getDerivedStateFromProps(
    nextProps: ErrorBoundaryProps,
    prevState: ErrorBoundaryState
  ) {
    // Check if the resetKeys prop has changed

    const propsKeys = nextProps.resetKeys;
    const stateKeys = prevState.resetKeys;

    if (JSON.stringify(propsKeys) !== JSON.stringify(stateKeys)) {
      // If it has, reset the error state
      return {
        error: null,
        resetKeys: propsKeys,
      };
    }

    return null; // No state update needed
  }

  state: ErrorBoundaryState = { error: null, recoveryAttempt: false };

  componentDidCatch(error: Error) {
    this.props.onError?.(error);
    if (this.props.recoverOnError && !this.state.recoveryAttempt) {
      // Re-throw asynchronously so Sentry (and other global error handlers) still
      // capture the error, even though we're attempting a silent recovery.
      window.queueMicrotask(() => {
        throw error;
      });

      this.props.recoverOnError(() => {
        this.setState({ error: null });
      });

      this.setState({ recoveryAttempt: true });
    } else if (this.props.logError) {
      this.props.logError(
        error,
        `ErrorBoundary: ${this.props.errorContextDescription || ''}`
      );
    } else {
      // Re-throw asynchronously to trigger global error handlers without breaking React rendering
      window.queueMicrotask(() => {
        throw error;
      });
    }
  }

  render() {
    if (this.state.error) {
      // You can render any custom fallback UI
      if (this.props.errorMessage) {
        return this.props.errorMessage;
      }

      return (
        <ErrorMessage
          title={this.props.title}
          description={this.props.description}
          apologyText={this.props.apologyText}
          contactSupportText={this.props.contactSupportText}
        >
          {isDevelopmentMode() && (
            <ErrorDetails
              error={this.state.error}
              stackTraceLabel={this.props.stackTraceLabel}
              stackTraceDevelopmentOnlyHint={
                this.props.stackTraceDevelopmentOnlyHint
              }
            />
          )}
        </ErrorMessage>
      );
    }

    return this.props.children;
  }
}

const ErrorDetailsOutput = styled.div`
  background-color: ${colors.bgSubtle};
  padding: ${space.large};
  border-radius: ${radius.small};
  font: 13px monospace;
  overflow-wrap: anywhere;
  white-space: pre;
  overflow: auto;
`;

type ErrorDetailsProps = {
  error: Error;
  stackTraceLabel?: string;
  stackTraceDevelopmentOnlyHint?: string;
};

const ErrorDetails = ({
  error,
  stackTraceLabel = 'Stack trace',
  stackTraceDevelopmentOnlyHint = '(Only shown in development mode)',
}: ErrorDetailsProps) => (
  <FlexBox flexDirection="column" gap="small" maxWidth="100ch">
    <FlexBox flexDirection="column" align="center" gap="xsmall">
      <Header2>{stackTraceLabel}</Header2>
      <Text variant="caption">{stackTraceDevelopmentOnlyHint}</Text>
    </FlexBox>
    {error.stack && <ErrorDetailsOutput>{error.stack}</ErrorDetailsOutput>}
  </FlexBox>
);

const isDevelopmentMode = () => import.meta.env.DEV;
